import cx from 'classnames';
import LoadingIndicator from 'components/LoadingIndicator';
import * as React from 'react';

import './styles/Field.scss';

/**
 * ## Wrapper component for a [Label](/internal/components/Label) & any input.
 * ### Helps ensure consistent layout & displays warnings & errors.
 *
 *
 * ```example
 * <div>
 *   <Field>
 *     <Label>Input label</Label>
 *     <Input placeholder="Input" />
 *   </Field>
 *   <Field warning>
 *     <Label>Input label</Label>
 *     <Input placeholder="Input in warning state" />
 *   </Field>
 *   <Field error="Input error message">
 *     <Label>Input label</Label>
 *     <Input placeholder="Input in error state" />
 *   </Field>
 * </div>
 * ```
 *
 * ```example
 * <div>
 *   <Field inline>
 *     <Checkbox label="Checkbox label" />
 *   </Field>
 *   <Field error inline>
 *     <Checkbox label="Checkbox label in error state" />
 *   </Field>
 * </div>
 * ```
 */

type Props = {
  children: React.ReactNode;
  className?: string;
  error?: any;
  flush?: boolean;
  inline?: boolean;
  loading?: boolean;
  subtext?: React.ReactNode;
  warning?: React.ReactNode;
};

export default class Field extends React.Component<Props> {
  render() {
    const { children, className, error, flush, inline, loading, subtext, warning } = this.props;
    let validation;

    if (typeof error === 'string') {
      validation = <div className="Field-validation Field-error">{error}</div>;
    } else if (warning) {
      validation = <div className="Field-validation Field-warning">{warning}</div>;
    }

    return (
      <div
        className={cx(
          {
            Field: true,
            'Field--flush': flush,
            'Field--hasError': !!error,
            'Field--hasWarning': !error && !!warning,
            'Field--inline': inline,
          },
          className
        )}
      >
        {children}
        {validation || (subtext && <div className="Field-validation">{subtext}</div>)}
        {loading && <LoadingIndicator className="Field-loading" />}
      </div>
    );
  }
}
